import CustomDatePicker from '@/components/shared/CustomDatePicker';
import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useBatchSelection } from '@/hooks/useBatchSelection';
import BatchActionBar from '@/components/ui/BatchActionBar';
import { Plus, PackageMinus, ChevronDown, ChevronUp, Pencil, Trash2, RefreshCw, ClipboardCheck, FileDown, UserCircle2, Navigation, Banknote, StickyNote } from 'lucide-react';
import { format } from 'date-fns';
import { id } from 'date-fns/locale';
import LastUpdated from '@/components/ui/LastUpdated';
import BarangKeluarManualForm from '@/components/delivery/BarangKeluarManualForm';
import BarangKeluarEditForm from '@/components/delivery/BarangKeluarEditForm';
import { exportToPDF } from '@/utils/exportPDF';
import { useSession } from '@/lib/SessionContext';
import { canManageDelivery } from '@/lib/AuthSession';

export default function BarangKeluarTab() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [syncing, setSyncing] = useState(false);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const { member } = useSession();
  const canEdit = canManageDelivery(member);

  const { data: records = [], isLoading } = useQuery({
    queryKey: ['barang-keluar'],
    queryFn: () => base44.entities.BarangKeluar.list('-created_date'),
  });

  const { data: orders = [] } = useQuery({
    queryKey: ['production-orders'],
    queryFn: () => base44.entities.ProductionOrder.list('-updated_date'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.BarangKeluar.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['barang-keluar'] }),
  });

  // Ref to track attempted syncs during this session
  const attemptedSyncIds = React.useRef(new Set());

  // PO yang sudah done dan belum pernah di-sync ke barang keluar
  const syncedPoData = useMemo(() => {
    const ids = new Set();
    const orderNumbers = new Set();
    records.forEach(r => { 
      if (r.source_po_id) ids.add(r.source_po_id); 
      if (r.nomor_referensi) {
        orderNumbers.add(r.nomor_referensi.replace(/^PO-/, '').trim().toLowerCase());
        orderNumbers.add(r.nomor_referensi.trim().toLowerCase());
      }
      if (r.catatan) {
        const match = r.catatan.match(/PO\s*#?([A-Za-z0-9_-]+)/i);
        if (match && match[1]) orderNumbers.add(match[1].trim().toLowerCase());
      }
    });
    return { ids, orderNumbers };
  }, [records]);

  const donePOs = useMemo(() =>
    orders.filter(o => {
      const oNum = (o.order_number || '').trim().toLowerCase();
      const isAlreadySynced = o.is_synced_to_delivery || 
        syncedPoData.ids.has(o.id) || 
        (oNum && syncedPoData.orderNumbers.has(oNum)) ||
        (oNum && syncedPoData.orderNumbers.has(`po-${oNum}`));

      if (o.status !== 'done' || isAlreadySynced) return false;
      const hasComps = (o.components || []).some(c => c.item_name);
      const hasRacikan = (o.racikan_digunakan || []).some(r => r.nama_bahan);
      return hasComps || hasRacikan;
    }),
    [orders, syncedPoData]
  );

  // Sync: baca PO done → buat record barang keluar per item (packaging + bahan cair)
  const handleSyncPO = async () => {
    const toSync = donePOs.filter(po => !attemptedSyncIds.current.has(po.id));
    if (toSync.length === 0) return;
    
    setSyncing(true);
    let anySynced = false;
    
    for (const po of toSync) {
      attemptedSyncIds.current.add(po.id);

      const poNum = (po.order_number || '').trim().toLowerCase();
      const alreadyInRecords = records.some(r => 
        r.source_po_id === po.id || 
        (poNum && (r.nomor_referensi || '').toLowerCase().includes(poNum)) ||
        (poNum && (r.catatan || '').toLowerCase().includes(poNum))
      );
      if (alreadyInRecords) {
        await base44.entities.ProductionOrder.update(po.id, { is_synced_to_delivery: true }).catch(() => {});
        continue;
      }

      const items = [];

      // Komponen packaging (botol, spray, tutup, dll)
      (po.components || []).forEach(comp => {
        if (!comp.item_name) return;
        items.push({
          nama_barang: comp.item_name,
          kategori: comp.category || comp.name || '-',
          jumlah: comp.qty_needed || 0,
          satuan: comp.unit || 'pcs',
          vendor: comp.vendor || '-',
          item_id: comp.inventory_item_id || '',
          item_tipe: comp.category || '',
        });
      });

      // Racikan / bahan cair
      (po.racikan_digunakan || []).forEach(r => {
        if (!r.nama_bahan) return;
        items.push({
          nama_barang: r.nama_bahan,
          kategori: r.kategori_bahan || 'Bahan Cair',
          jumlah: r.kebutuhan_nilai || 0,
          satuan: r.kebutuhan_satuan || 'g',
          vendor: r.vendor || '-',
          item_id: '',
          item_tipe: 'bahan',
        });
      });

      if (items.length === 0) continue;

      const sudahDikurangi = po.stock_deducted
        ? ` — Stok sudah dikurangi otomatis (${po.stock_deducted_trigger === 'in_progress' ? 'saat In Progress' : 'saat Selesai'})`
        : '';

      try {
        await base44.entities.BarangKeluar.create({
          nomor_referensi: `PO-${po.order_number || po.id.slice(-6)}`,
          tanggal: po.shipped_at || po.shipping_date || new Date().toISOString().split('T')[0],
          tujuan: po.brand_name || '',
          alasan: 'Produksi (PO)',
          catatan: `Auto-sync dari PO #${po.order_number} — ${po.brand_name} ${po.product_name || ''}${sudahDikurangi}`.trim(),
          source_po_id: po.id,
          items,
        });
        
        // Mark the PO as synced so it won't be synced again even if the user deletes the BarangKeluar record
        await base44.entities.ProductionOrder.update(po.id, { is_synced_to_delivery: true });
        anySynced = true;
      } catch (err) {
        console.error("Gagal sync PO ke Barang Keluar", err);
      }
    }
    
    if (anySynced) {
      await queryClient.invalidateQueries({ queryKey: ['barang-keluar'] });
      await queryClient.invalidateQueries({ queryKey: ['production-orders'] });
    }
    setSyncing(false);
  };

  // Background auto-sync completely disabled as per user instruction to prevent duplicate/repeated data fetching

  const filtered = records.filter(r => {
    const q = search.toLowerCase();
    const matchSearch = !search ||
      r.nomor_referensi?.toLowerCase().includes(q) ||
      r.tujuan?.toLowerCase().includes(q) ||
      r.alasan?.toLowerCase().includes(q) ||
      r.pic?.toLowerCase().includes(q) ||
      r.catatan?.toLowerCase().includes(q) ||
      r.ekspedisi?.toLowerCase().includes(q) ||
      (r.items || []).some(it => 
        it.nama_barang?.toLowerCase().includes(q) ||
        it.nama?.toLowerCase().includes(q) ||
        it.kategori?.toLowerCase().includes(q)
      );
    const matchFrom = !dateFrom || (r.tanggal && r.tanggal >= dateFrom);
    const matchTo = !dateTo || (r.tanggal && r.tanggal <= dateTo);
    return matchSearch && matchFrom && matchTo;
  });

  const handleDelete = (record) => {
    if (!confirm(`Hapus catatan barang keluar "${record.nomor_referensi || 'ini'}"?`)) return;
    deleteMutation.mutate(record.id);
  };

  const {
    selectedIds,
    isSelectionMode,
    toggleSelection,
    selectAll,
    clearSelection,
    startSelection,
    selectedCount
  } = useBatchSelection(filtered, o => o.id);

  const handleBulkDelete = async () => {
    if (!confirm(`Hapus ${selectedCount} catatan barang keluar terpilih?`)) return;
    await Promise.all(Array.from(selectedIds).map(id => deleteMutation.mutateAsync(id)));
    clearSelection();
  };

  const handleExport = () => {
    const subtitle = (dateFrom || dateTo)
      ? `Periode: ${dateFrom || '...'} s/d ${dateTo || '...'} · ${filtered.length} data`
      : `Total ${filtered.length} catatan pengeluaran`;
    exportToPDF({
      title: 'Laporan Barang Keluar',
      subtitle,
      filename: 'Barang-Keluar',
      columns: [
        { key: 'nomor_referensi', label: 'No. Referensi' },
        { key: 'tanggal', label: 'Tanggal', render: r => r.tanggal ? format(new Date(r.tanggal + 'T00:00:00'), 'dd/MM/yyyy') : '-' },
        { key: 'tujuan', label: 'Tujuan', render: r => r.tujuan || '-' },
        { key: 'alasan', label: 'Alasan', render: r => r.alasan || '-' },
        { key: 'ekspedisi', label: 'Ekspedisi', render: r => r.ekspedisi || '-' },
        { key: 'ongkir', label: 'Ongkir', render: r => {
          if (!r.ongkir || r.ongkir <= 0) return '-';
          const dibayar = r.ongkir_dibayar_oleh === 'Perusahaan' ? 'dari Perusahaan' : r.ongkir_dibayar_oleh === 'Customer' ? 'dari Client' : r.ongkir_dibayar_oleh === 'Split' ? '(Split)' : '';
          return `Rp ${r.ongkir.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${dibayar}`;
        }},
        { key: 'items', label: 'Jml Item', render: r => `${r.items?.length || 0} item` },
        { key: 'items_detail', label: 'Detail Barang', render: r => (r.items || []).map(i => `${i.nama_barang} (-${i.jumlah} ${i.satuan})`).join(', ') || '-' },
        { key: 'pic', label: 'PIC', render: r => r.pic || '-' },
        { key: 'catatan', label: 'Catatan', render: r => r.catatan || '-' },
      ],
      rows: filtered,
    });
  };

  return (
    <div className="space-y-4">
      {/* Sync PO is now automated in the background */}

      <div className="flex flex-col md:flex-row gap-3 md:gap-2 items-stretch md:items-center">
        <Input
          placeholder="Cari nama item, tujuan, no referensi..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full md:w-64 h-9 md:h-10 text-xs md:text-sm"
        />
        <div className="flex flex-row items-center gap-2">
          <div className="flex items-center gap-1.5 flex-1 md:flex-none">
            <span className="text-[11px] md:text-xs text-muted-foreground whitespace-nowrap hidden sm:inline">Dari:</span>
            <CustomDatePicker value={dateFrom} onChange={setDateFrom} className="w-full md:w-36 h-9 text-xs" />
          </div>
          <div className="flex items-center gap-1.5 flex-1 md:flex-none">
            <span className="text-[11px] md:text-xs text-muted-foreground whitespace-nowrap hidden sm:inline">S/d:</span>
            <CustomDatePicker value={dateTo} onChange={setDateTo} className="w-full md:w-36 h-9 text-xs" />
          </div>
          {(dateFrom || dateTo) && (
            <Button variant="ghost" size="sm" className="text-xs h-9 px-2 shrink-0" onClick={() => { setDateFrom(''); setDateTo(''); }}>Reset</Button>
          )}
        </div>
        <div className="flex gap-2 shrink-0 w-full md:w-auto md:ml-auto">
          <Button onClick={handleExport} variant="outline" className="gap-1.5 flex-1 md:flex-none h-9 text-xs md:text-sm">
            <FileDown className="w-3.5 h-3.5 md:w-4 md:h-4" /> <span className="hidden sm:inline">Export PDF</span><span className="sm:hidden">PDF</span>
          </Button>
          {canEdit && (
            <Button onClick={() => setShowForm(true)} className="gap-1.5 flex-1 md:flex-none h-9 text-xs md:text-sm">
              <Plus className="w-3.5 h-3.5 md:w-4 md:h-4" /> <span className="hidden sm:inline">Input Barang Keluar</span><span className="sm:hidden">Input Keluar</span>
            </Button>
          )}
        </div>
      </div>

      <BatchActionBar
        isSelectionMode={isSelectionMode}
        selectedCount={selectedCount}
        totalCount={filtered.length}
        onSelectAll={selectAll}
        onClearSelection={clearSelection}
        onBatchDelete={handleBulkDelete}
      />

      {isLoading ? (
        <div className="text-center py-8 md:py-12 text-sm text-muted-foreground">Memuat data...</div>
      ) : (
        <div className="grid gap-3">
          {filtered.length === 0 ? (
            <div className="text-center py-8 md:py-12 text-sm text-muted-foreground">Belum ada data barang keluar.</div>
          ) : (
            filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map(record => (
              <Card 
                key={record.id}
                className={`transition-all ${
                  isSelectionMode && selectedIds.has(record.id) ? 'ring-2 ring-rose-500 bg-rose-50/20' : ''
                }`}
                onClick={() => isSelectionMode && toggleSelection(record.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      {isSelectionMode && (
                        <div className="shrink-0 animate-in fade-in slide-in-from-left-2 duration-200" onClick={(e) => e.stopPropagation()}>
                          <Checkbox 
                            checked={selectedIds.has(record.id)} 
                            onCheckedChange={() => toggleSelection(record.id)}
                            className="data-[state=checked]:bg-rose-500 data-[state=checked]:border-rose-500"
                          />
                        </div>
                      )}
                      <div className="w-10 h-10 rounded-lg bg-red-100 text-red-600 flex items-center justify-center shrink-0">
                        <PackageMinus className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold">{record.nomor_referensi || 'Tanpa Referensi'}</span>
                          <Badge variant="outline" className="text-xs text-red-600 border-red-300">
                            {record.items?.length || 0} item
                          </Badge>
                          {record.alasan && (
                            <Badge variant="secondary" className="text-xs">{record.alasan}</Badge>
                          )}
                          {record.source_po_id && (
                            <Badge className="text-xs bg-blue-100 text-blue-700 border-blue-200">dari PO</Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {record.tanggal ? format(new Date(record.tanggal + 'T00:00:00'), 'dd MMMM yyyy', { locale: id }) : '-'}
                          {record.tujuan ? ` • ${record.tujuan}` : ''}
                        </p>
                        {record.pic && (
                          <span className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium mt-0.5">
                            <UserCircle2 className="w-3 h-3" />{record.pic}
                          </span>
                        )}
                        <LastUpdated date={record.updated_date || record.created_date} className="mt-0.5" />
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {canEdit && !isSelectionMode && (
                        <>
                          <Button variant="ghost" size="icon" className="w-8 h-8 text-muted-foreground hover:text-foreground"
                            onClick={(e) => { e.stopPropagation(); setEditRecord(record); }}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="w-8 h-8 text-muted-foreground hover:text-destructive"
                            onClick={(e) => { e.stopPropagation(); startSelection(record.id); }}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      <Button variant="ghost" size="icon" className="w-8 h-8"
                        onClick={() => setExpanded(expanded === record.id ? null : record.id)}>
                        {expanded === record.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  {expanded === record.id && (
                    <div className="mt-4 border-t pt-4 space-y-3">
                      {/* Info ekspedisi & ongkir */}
                      {(record.ekspedisi || record.ongkir > 0 || record.pic) && (
                        <div className="flex flex-wrap gap-2 text-xs">
                          {record.ekspedisi && (
                            <span className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-700 px-2.5 py-1 rounded-full font-medium">
                              <Navigation className="w-3 h-3" />{record.ekspedisi}
                            </span>
                          )}
                          {record.ongkir > 0 && (
                            <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-full font-medium">
                              <Banknote className="w-3 h-3" />Rp {record.ongkir.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              {record.ongkir_dibayar_oleh && <span className="text-emerald-500">· {record.ongkir_dibayar_oleh}</span>}
                            </span>
                          )}
                          {record.pic && (
                            <span className="inline-flex items-center gap-1.5 bg-primary/10 text-primary px-2.5 py-1 rounded-full font-medium">
                              <UserCircle2 className="w-3 h-3" />{record.pic}
                            </span>
                          )}
                        </div>
                      )}
                      {record.catatan && <p className="text-sm text-muted-foreground italic">Catatan: {record.catatan}</p>}
                      {record.items?.length > 0 && (
                        <>
                          <div className="grid grid-cols-4 text-xs font-semibold text-muted-foreground pb-1 border-b">
                            <span className="col-span-2">Barang</span>
                            <span className="text-center">Vendor</span>
                            <span className="text-right">Jumlah</span>
                          </div>
                          {Object.values((record.items || []).reduce((acc, item) => {
                            const key = `${item.nama_barang}_${item.satuan}`;
                            if (!acc[key]) acc[key] = { ...item, jumlah: Number(item.jumlah) };
                            else acc[key].jumlah += Number(item.jumlah);
                            return acc;
                          }, {})).map((item, idx) => (
                            <div key={idx} className="grid grid-cols-4 text-sm items-center">
                              <div className="col-span-2">
                                <span className="font-medium">{item.nama_barang}</span>
                                <span className="ml-1.5">
                                  <Badge variant="outline" className="text-xs">{item.kategori}</Badge>
                                </span>
                              </div>
                              <span className="text-center text-xs text-muted-foreground">{item.vendor || '-'}</span>
                              <span className="text-right font-medium text-red-600">-{Number.isInteger(item.jumlah) ? item.jumlah : item.jumlah.toFixed(3)} {item.satuan}</span>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}

          {/* ── Pagination Controls ── */}
          {filtered.length > 0 && (
            <div className="px-6 py-4 border border-slate-100 rounded-2xl bg-white flex flex-wrap items-center justify-between gap-4 mt-6">
              <div className="flex items-center gap-3">
                <span className="text-[11px] font-bold text-slate-500">Row per Page</span>
                <div className="w-[80px]">
                  <Select value={String(itemsPerPage)} onValueChange={(val) => { setItemsPerPage(Number(val)); setCurrentPage(1); }}>
                    <SelectTrigger className="h-8 text-[11px] font-bold text-slate-700 bg-slate-50 border-slate-200/80 rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[5, 10, 25, 50, 100].map(n => (
                        <SelectItem key={n} value={String(n)} className="text-[11px] font-bold">{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex items-center gap-1.5">
                <button 
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-[#8C6208] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 transition-all font-bold text-sm"
                >
                  &lt;
                </button>
                
                {(() => {
                  const totalPages = Math.ceil(filtered.length / itemsPerPage);
                  const pages = [];
                  for(let i=1; i<=totalPages; i++) {
                    if (i === 1 || i === totalPages || (i >= currentPage - 1 && i <= currentPage + 1)) {
                      pages.push(
                        <button
                          key={i}
                          onClick={() => setCurrentPage(i)}
                          className={`w-8 h-8 flex items-center justify-center rounded-lg text-[11px] font-black transition-all ${
                            currentPage === i 
                              ? 'bg-[#8C6208] text-white shadow-md shadow-[#8C6208]/30' 
                              : 'text-slate-500 hover:bg-[#F9F5EC] hover:text-[#8C6208]'
                          }`}
                        >
                          {i}
                        </button>
                      );
                    } else if (i === currentPage - 2 || i === currentPage + 2) {
                      pages.push(<span key={i} className="text-slate-400 text-[10px] px-1">...</span>);
                    }
                  }
                  return pages;
                })()}
                
                <button 
                  onClick={() => setCurrentPage(p => Math.min(Math.ceil(filtered.length / itemsPerPage), p + 1))}
                  disabled={currentPage >= Math.ceil(filtered.length / itemsPerPage)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-[#8C6208] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 transition-all font-bold text-sm"
                >
                  &gt;
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Floating Back to Top Button */}
      <button 
        onClick={scrollToTop}
        className="fixed bottom-[80px] md:bottom-8 right-6 md:right-8 z-50 w-12 h-12 bg-[#8C6208] hover:bg-[#6A4A06] text-white rounded-full shadow-xl shadow-[#8C6208]/30 flex items-center justify-center transition-transform hover:-translate-y-1 print:hidden border border-[#D4AF37]/30"
        title="Kembali ke Atas"
      >
        <ChevronUp className="w-6 h-6" />
      </button>

      {showForm && (
        <BarangKeluarManualForm
          onClose={() => setShowForm(false)}
          onSaved={() => {
            setShowForm(false);
            queryClient.invalidateQueries({ queryKey: ['barang-keluar'] });
          }}
        />
      )}

      {editRecord && (
        <BarangKeluarEditForm
          record={editRecord}
          onClose={() => setEditRecord(null)}
          onSaved={() => {
            setEditRecord(null);
            queryClient.invalidateQueries({ queryKey: ['barang-keluar'] });
          }}
        />
      )}
    </div>
  );
}